import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { validateInvite, forgotPassword, resetPassword, verifyResetToken } from '../services/auth';
import { FIRA_WW_LOGO } from '../context/WorldwideContext';
import { AlertCircle, ArrowLeft, CheckCircle, Eye, EyeOff, Gift, KeyRound, LogIn, Rocket, Shield, TrendingUp, UserPlus, Zap } from 'lucide-react';

// Subtle Gradient Pulse — CSS-only, lightweight & professional
const GradientPulseBackground = () => {
  const keyframes = `
    @keyframes pulseOrb1 {
      0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.15; }
      50% { transform: translate(30px, -20px) scale(1.15); opacity: 0.25; }
    }
    @keyframes pulseOrb2 {
      0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.10; }
      50% { transform: translate(-25px, 15px) scale(1.1); opacity: 0.18; }
    }
    @keyframes pulseOrb3 {
      0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.08; }
      50% { transform: translate(15px, 25px) scale(1.2); opacity: 0.14; }
    }
    @keyframes shimmerLine {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <style>{particleKeyframes}</style>
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 0, overflow: 'hidden', background: '#050508',
      }}>
        {/* Base subtle gradient */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 50% 40%, rgba(20, 20, 40, 0.8) 0%, transparent 70%)',
        }} />

        {/* Orb 1 — deep blue, top-right */}
        <div style={{
          position: 'absolute', top: '-15%', right: '-10%',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(30, 60, 120, 0.4) 0%, transparent 70%)',
          filter: 'blur(80px)',
          animation: 'pulseOrb1 8s ease-in-out infinite',
        }} />

        {/* Orb 2 — muted rose, bottom-left */}
        <div style={{
          position: 'absolute', bottom: '-20%', left: '-5%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(120, 40, 60, 0.3) 0%, transparent 70%)',
          filter: 'blur(90px)',
          animation: 'pulseOrb2 10s ease-in-out infinite',
        }} />

        {/* Orb 3 — subtle warm accent, center */}
        <div style={{
          position: 'absolute', top: '30%', left: '40%',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(80, 50, 100, 0.2) 0%, transparent 70%)',
          filter: 'blur(100px)',
          animation: 'pulseOrb3 12s ease-in-out infinite',
        }} />

        {/* Subtle horizontal shimmer line */}
        <div style={{
          position: 'absolute', top: '45%', left: 0, width: '100%', height: '1px',
          overflow: 'hidden', opacity: 0.06,
        }}>
          <div style={{
            width: '50%', height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
            animation: 'shimmerLine 6s ease-in-out infinite',
          }} />
        </div>

        {/* Fine grain noise overlay for texture */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }} />

        {/* Futuristic perspective grid */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%',
          background: `
            linear-gradient(180deg, transparent 0%, rgba(255,64,129,0.05) 100%),
            repeating-linear-gradient(90deg, rgba(255,64,129,0.1) 0px, transparent 1px, transparent 50px),
            repeating-linear-gradient(0deg, rgba(255,64,129,0.07) 0px, transparent 1px, transparent 35px)
          `,
          transform: 'perspective(500px) rotateX(55deg)',
          transformOrigin: 'bottom center',
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
        }} />

        {/* Floating particles — bigger, brighter */}
        {[...Array(15)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: `${4 + (i % 4) * 2}px`,
            height: `${4 + (i % 4) * 2}px`,
            borderRadius: '50%',
            background: i % 3 === 0 ? 'rgba(255,64,129,0.7)' : i % 3 === 1 ? 'rgba(34,211,238,0.6)' : 'rgba(251,191,36,0.6)',
            left: `${5 + (i * 6.2)}%`,
            top: `${10 + ((i * 13) % 75)}%`,
            animation: `floatParticle${(i % 3) + 1} ${5 + (i % 4) * 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`,
            boxShadow: `0 0 ${8 + (i % 3) * 4}px ${i % 3 === 0 ? 'rgba(255,64,129,0.5)' : i % 3 === 1 ? 'rgba(34,211,238,0.4)' : 'rgba(251,191,36,0.4)'}`,
          }} />
        ))}

        {/* Horizontal scan line — brighter */}
        <div style={{
          position: 'absolute', left: 0, right: 0, height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(255,64,129,0.3), rgba(34,211,238,0.2), transparent)',
          animation: 'scanLine 8s linear infinite',
        }} />
      </div>
    </>
  );
};

// Add particle + scan animations to keyframes
const particleKeyframes = `
  @keyframes floatParticle1 {
    0%, 100% { transform: translateY(0) translateX(0); opacity: 0.4; }
    50% { transform: translateY(-30px) translateX(10px); opacity: 0.8; }
  }
  @keyframes floatParticle2 {
    0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
    50% { transform: translateY(-20px) translateX(-15px); opacity: 0.7; }
  }
  @keyframes floatParticle3 {
    0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
    50% { transform: translateY(-25px) translateX(8px); opacity: 0.6; }
  }
  @keyframes scanLine {
    0% { top: 10%; }
    100% { top: 90%; }
  }
`;

const AuthPage = ({ worldwideMode = false }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isBM = worldwideMode ? false : (language === 'bm' || language === 'ms');
  
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Forgot/Reset Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  
  // Invite system state
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [validatingInvite, setValidatingInvite] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    newPassword: '',
    confirmPassword: ''
  });

  const { login, register, registerWithInvite, registerWorldwideTrial } = useAuth();

  // Check for invite code or reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invite = urlParams.get('invite');
    const token = urlParams.get('token');
    
    if (invite) {
      setInviteCode(invite);
      setIsLogin(false); // Switch to register mode
      validateInviteCode(invite);
    }
    
    // Check for password reset token
    if (token && window.location.pathname.includes('reset-password')) {
      setResetToken(token);
      setShowResetPassword(true);
      verifyResetTokenHandler(token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateInviteCode = async (code) => {
    setValidatingInvite(true);
    setInviteError('');
    try {
      const result = await validateInvite(code);
      if (result.valid) {
        setInviteData(result);
      } else {
        setInviteError(isBM ? 'Invite link is invalid or has expired' : 'Invite link is invalid or has expired');
        setInviteCode(null);
      }
    } catch (err) {
      const message = err.response?.data?.detail || (isBM ? 'Invalid invite link' : 'Invalid invite link');
      setInviteError(message);
      setInviteCode(null);
    } finally {
      setValidatingInvite(false);
    }
  };

  const verifyResetTokenHandler = async (token) => {
    try {
      const result = await verifyResetToken(token);
      if (result.valid) {
        setResetEmail(result.email);
      }
    } catch (err) {
      setError(isBM ? 'Reset link is invalid or has expired. Please request a new link.' : 'Reset link is invalid or has expired. Please request a new link.');
      setShowResetPassword(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await forgotPassword(formData.email);
      setSuccess(result.message || (isBM ? 'If email is registered, you will receive a password reset link.' : 'If email is registered, you will receive a password reset link.'));
      setFormData({ ...formData, email: '' });
    } catch (err) {
      setError(err.response?.data?.detail || (isBM ? 'An error occurred. Please try again.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError(isBM ? 'Passwords do not match' : 'Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordErrors = [];
    if (formData.newPassword.length < 8) passwordErrors.push(isBM ? 'minimum 8 characters' : 'minimum 8 characters');
    if (!/[A-Z]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 uppercase letter' : '1 uppercase letter');
    if (!/[a-z]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 lowercase letter' : '1 lowercase letter');
    if (!/[0-9]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 number' : '1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 symbol' : '1 symbol');

    if (passwordErrors.length > 0) {
      setError((isBM ? 'Password must contain: ' : 'Password must contain: ') + passwordErrors.join(', '));
      return;
    }

    setLoading(true);

    try {
      const result = await resetPassword(resetToken, formData.newPassword);
      setSuccess(result.message || (isBM ? 'Password successfully updated!' : 'Password successfully updated!'));
      // Redirect to login after 2 seconds
      setTimeout(() => {
        setShowResetPassword(false);
        setResetToken(null);
        setIsLogin(true);
        window.history.pushState({}, '', '/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || (isBM ? 'An error occurred. Please try again.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (!result.success) {
          setError(result.error);
        } else {
          // Redirect to dashboard on successful login (preserve ?tab= param)
          const tabParam = new URLSearchParams(window.location.search).get('tab');
          const dashPath = worldwideMode ? '/worldwide/dashboard' : '/dashboard';
          navigate(tabParam ? `${dashPath}?tab=${tabParam}` : dashPath);
        }
      } else {
        // Password strength validation
        const passwordErrors = [];
        if (formData.password.length < 8) {
          passwordErrors.push(isBM ? 'minimum 8 characters' : 'minimum 8 characters');
        }
        if (!/[A-Z]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 uppercase letter' : '1 uppercase letter');
        }
        if (!/[a-z]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 lowercase letter' : '1 lowercase letter');
        }
        if (!/[0-9]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 number' : '1 number');
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 symbol (!@#$%^&*)' : '1 symbol (!@#$%^&*)');
        }
        
        if (passwordErrors.length > 0) {
          setError((isBM ? 'Password must contain: ' : 'Password must contain: ') + passwordErrors.join(', '));
          setLoading(false);
          return;
        }
        
        // Registration — Invite Only
        // Worldwide self-service trial: no invite needed — just email/password/name.
        // AuthContext handles setting user + isAuthenticated state, which triggers
        // AuthPageWrapper to redirect to /worldwide/dashboard automatically.
        if (worldwideMode && !inviteCode) {
          const result = await registerWorldwideTrial(
            formData.email,
            formData.password,
            formData.name,
          );
          if (!result.success) {
            setError(result.error || 'Registration failed');
          }
          // Success → AuthPageWrapper auto-redirects because isAuthenticated flipped true.
          setLoading(false);
          return;
        }

        if (!inviteCode || !inviteData) {
          setError(isBM ? 'Invite link diperlukan. Hubungi admin untuk akses.' : 'Invite link required. Contact admin for access.');
          setLoading(false);
          return;
        }
        
        const result = await registerWithInvite(
          inviteCode, 
          formData.email, 
          formData.password, 
          formData.name, 
          formData.phone || null
        );
        if (!result.success) {
          setError(result.error);
        } else {
          const tabParam2 = new URLSearchParams(window.location.search).get('tab');
          const dashPath2 = worldwideMode ? '/worldwide/dashboard' : '/dashboard';
          navigate(tabParam2 ? `${dashPath2}?tab=${tabParam2}` : dashPath2);
        }
      }
    } catch (err) {
      // Catch any remaining unhandled errors with user-friendly messages
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Server is taking too long to respond. Please try again.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setError('Unable to connect to server. Please check your internet connection.');
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: TrendingUp, text: 'Institutional Signals' },
    { icon: Shield, text: 'Bank-Grade Encryption' },
    { icon: Zap, text: 'Volume Profile Analysis' }
  ];

  // Fira Smart Desk Pink Theme (matching the app)
  const colors = {
    bgPrimary: '#000000',           // Black background
    bgCard: '#1A1A1A',              // Dark card background
    bgInput: '#2A2A2A',             // Input background
    accent: '#FF4081',              // Pink/Magenta accent
    accentLight: '#FF80AB',         // Light pink
    accentGradient: 'linear-gradient(135deg, #FF4081, #FF80AB)', // Pink gradient
    textPrimary: '#FFFFFF',         // White text
    textSecondary: '#E0E0E0',       // Light gray text
    textMuted: '#9E9E9E',           // Muted text
    border: '#333333',              // Border color
    borderFocus: '#FF4081',         // Focus border (pink)
    error: '#FF5252',               // Error red
    success: '#69F0AE',             // Success green
    invite: '#3B82F6',              // Invite blue
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#000000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px 16px 32px',
      position: 'relative',
      overflowY: 'auto',
    }}>
      <GradientPulseBackground />
      <div style={{
        width: '100%',
        maxWidth: '500px',
        position: 'relative',
        zIndex: 1,
      }}>
        
        {/* Logo and Branding */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
        }}>
          {/* Worldwide: show actual logo image for visual differentiation */}
          {worldwideMode && (
            <img
              src={FIRA_WW_LOGO}
              alt="Fira Worldwide"
              data-testid="auth-ww-logo"
              style={{
                width: '120px',
                height: '120px',
                margin: '0 auto 16px',
                borderRadius: '20px',
                display: 'block',
                boxShadow: '0 12px 32px rgba(255, 64, 129, 0.25)',
              }}
            />
          )}
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            background: colors.accentGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '4px',
          }}>
            {worldwideMode ? 'Fira Worldwide' : 'Fira Smart Desk'}
          </h1>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '10px',
          }}>
            <div style={{ height: '1px', width: '40px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.2))' }} />
            <span style={{
              fontSize: '0.65rem',
              fontWeight: '600',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)',
            }}>
              {worldwideMode ? 'GLOBAL EDITION' : 'INSTITUTIONAL GRADE'}
            </span>
            <div style={{ height: '1px', width: '40px', background: 'linear-gradient(to left, transparent, rgba(255,255,255,0.2))' }} />
          </div>
          <p style={{ 
            color: colors.accent, 
            fontSize: '0.8rem', 
            fontStyle: 'italic',
            fontWeight: '500',
            letterSpacing: '0.5px',
            marginBottom: '8px'
          }}>
            "Where AI meets Wall Street precision."
          </p>
          <p style={{ color: colors.textMuted, fontSize: '0.78rem' }}>
            {worldwideMode ? 'AI-Powered Trading Signal Platform' : (isBM ? 'Institutional-Grade AI Trading System for Malaysian Traders' : 'Institutional-Grade AI Trading System for Malaysian Traders')}
          </p>
        </div>

        {/* Invite Banner */}
        {validatingInvite && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.invite}15`,
            border: `1px solid ${colors.invite}40`,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid',
              borderColor: `${colors.invite} transparent ${colors.invite} transparent`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <span style={{ color: colors.invite, fontSize: '0.9rem' }}>
              {isBM ? 'Verifying invite link...' : 'Verifying invite link...'}
            </span>
          </div>
        )}

        {inviteData && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.success}15`,
            border: `1px solid ${colors.success}40`,
            borderRadius: '12px',
          }} data-testid="invite-valid-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <Gift size={20} style={{ color: colors.success }} />
              <span style={{ color: colors.success, fontWeight: '600', fontSize: '0.95rem' }}>
                {isBM ? 'Valid Invite Link!' : 'Valid Invite Link!'}
              </span>
            </div>
            <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
              {isBM 
                ? <>Selamat datang, <strong>{inviteData.client_name}</strong>! Sila lengkapkan pendaftaran anda di bawah.</>
                : <>Welcome, <strong>{inviteData.client_name}</strong>! Please complete your registration below.</>}
            </p>
          </div>
        )}

        {inviteError && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.error}15`,
            border: `1px solid ${colors.error}40`,
            borderRadius: '12px',
          }} data-testid="invite-error-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertCircle size={20} style={{ color: colors.error }} />
              <span style={{ color: colors.error, fontSize: '0.9rem' }}>
                {inviteError}
              </span>
            </div>
          </div>
        )}

        {/* Auth Card */}
        <div style={{
          background: colors.accentGradient,
          borderRadius: '24px',
          padding: '2px',
          boxShadow: '0 0 40px rgba(255, 64, 129, 0.15), 0 0 80px rgba(255, 64, 129, 0.05)',
        }}>
          <div style={{
            backgroundColor: 'rgba(20, 20, 20, 0.92)',
            backdropFilter: 'blur(20px)',
            borderRadius: '22px',
            padding: '32px',
          }}>
            
            {/* Welcome Message */}
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
              }}>
                <Rocket style={{ width: '24px', height: '24px', color: colors.accent }} />
                <span style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: '600', 
                  color: colors.textPrimary 
                }}>
                  {inviteData 
                    ? (isBM ? 'Invite Registration' : 'Invite Registration')
                    : (isLogin 
                      ? (isBM ? 'Welcome Back!' : 'Welcome Back!') 
                      : (isBM ? 'Sertai Kami!' : 'Join Us!'))}
                </span>
              </div>
              <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                {inviteData 
                  ? (isBM ? 'Complete your information to register' : 'Complete your information to register your account')
                  : (isLogin 
                    ? (isBM ? 'Login to access your trading dashboard' : 'Login to access your trading dashboard')
                    : (isBM ? 'Create an account to start trading smart' : 'Register an account to start trading wisely'))}
              </p>
            </div>

            {/* Tab Switcher - Hide when invite is active */}
            {!inviteData && (
              <>
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '24px',
                backgroundColor: colors.bgInput,
                padding: '4px',
                borderRadius: '12px',
              }}>
                <button
                  onClick={() => { setIsLogin(true); setError(''); }}
                  data-testid="login-tab"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    border: 'none',
                    cursor: 'pointer',
                    background: isLogin ? colors.accentGradient : 'transparent',
                    color: isLogin ? '#FFFFFF' : colors.textMuted,
                    fontWeight: isLogin ? '600' : '400',
                    fontSize: '0.9375rem',
                  }}
                >
                  <LogIn style={{ width: '16px', height: '16px' }} />
                  {isBM ? 'Login' : 'Login'}
                </button>
                <button
                  onClick={() => { setIsLogin(false); setError(''); }}
                  data-testid="register-tab"
                  disabled={!inviteCode && !worldwideMode}
                  title={(!inviteCode && !worldwideMode) ? (isBM ? 'Hubungi admin untuk invite link' : 'Contact admin for invite link') : ''}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    border: 'none',
                    cursor: (!inviteCode && !worldwideMode) ? 'not-allowed' : 'pointer',
                    background: !isLogin ? colors.accentGradient : 'transparent',
                    color: !isLogin ? '#FFFFFF' : colors.textMuted,
                    fontWeight: !isLogin ? '600' : '400',
                    fontSize: '0.9375rem',
                    opacity: (!inviteCode && !worldwideMode) ? 0.5 : 1,
                  }}
                >
                  <UserPlus style={{ width: '16px', height: '16px' }} />
                  {worldwideMode && !inviteCode ? 'Sign Up Free' : 'Register'}
                </button>
              </div>
              
              {/* Registration is now open */}
            </>
            )}

            {/* Worldwide self-service banner — shown on Register tab when no invite */}
            {!isLogin && !inviteCode && worldwideMode && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1), rgba(74, 222, 128, 0.08))',
                border: '1px solid rgba(74, 222, 128, 0.3)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#4ADE80', fontWeight: '700', marginBottom: '6px', fontSize: '14px' }}>
                  🎁 Start Your 5-Day Free Trial
                </div>
                <div style={{ color: colors.textMuted, fontSize: '12px', lineHeight: '1.5' }}>
                  Full Smart Desk access — AI Signals, Premium features, Smart Capital preview. No credit card, no invite code.
                </div>
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#FBBF24' }}>
                  After 5 days: unlock continued access via partner broker deposit.
                </div>
              </div>
            )}

            {/* Registration — Invite Only (Malaysian portal) */}
            {!isLogin && !inviteCode && !worldwideMode && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: 'rgba(255, 64, 129, 0.1)',
                border: '1px solid rgba(255, 64, 129, 0.3)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ color: colors.accent, fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                  {isBM ? 'Pendaftaran Eksklusif' : 'Exclusive Registration'}
                </div>
                <div style={{ color: colors.textMuted, fontSize: '12px', lineHeight: '1.5' }}>
                  {isBM 
                    ? 'Daftar akaun trading bawah IB kami untuk mendapat akses penuh ke platform.'
                    : 'Register a trading account under our IB to get full platform access.'}
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#F59E0B' }}>
                  {isBM ? 'Hubungi admin untuk dapatkan invite link.' : 'Contact admin to get invite link.'}
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={{
                marginBottom: '20px',
                padding: '14px',
                backgroundColor: 'rgba(255, 82, 82, 0.1)',
                border: '1px solid rgba(255, 82, 82, 0.3)',
                borderRadius: '10px',
                color: colors.error,
                fontSize: '0.875rem',
              }} data-testid="auth-error">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div style={{
                marginBottom: '20px',
                padding: '14px',
                backgroundColor: 'rgba(105, 240, 174, 0.1)',
                border: '1px solid rgba(105, 240, 174, 0.3)',
                borderRadius: '10px',
                color: colors.success,
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }} data-testid="auth-success">
                <CheckCircle size={18} />
                {success}
              </div>
            )}

            {/* FORGOT PASSWORD FORM */}
            {showForgotPassword && !showResetPassword && (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '20px',
                  cursor: 'pointer'
                }} onClick={() => { setShowForgotPassword(false); setError(''); setSuccess(''); }}>
                  <ArrowLeft size={18} style={{ color: colors.accent }} />
                  <span style={{ color: colors.accent, fontSize: '0.9rem' }}>{isBM ? 'Back to Login' : 'Back to Login'}</span>
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <KeyRound size={40} style={{ color: colors.accent, marginBottom: '12px' }} />
                  <h3 style={{ color: colors.textPrimary, margin: '0 0 8px', fontSize: '1.25rem' }}>
                    {isBM ? 'Lupa Kata Laluan?' : 'Forgot Password?'}
                  </h3>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
                    {isBM ? 'Masukkan email anda dan kami akan hantar link untuk reset kata laluan.' : 'Enter your email and we will send a password reset link.'}
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        backgroundColor: colors.bgInput,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        color: colors.textPrimary,
                        fontSize: '1rem',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      placeholder="email@example.com"
                      data-testid="forgot-email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.accentGradient,
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '1rem',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(255, 64, 129, 0.4)',
                    }}
                  >
                    {loading ? (isBM ? 'Menghantar...' : 'Sending...') : (isBM ? 'Send Reset Link' : 'Send Reset Link')}
                  </button>
                </form>
              </div>
            )}

            {/* RESET PASSWORD FORM */}
            {showResetPassword && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <KeyRound size={40} style={{ color: colors.accent, marginBottom: '12px' }} />
                  <h3 style={{ color: colors.textPrimary, margin: '0 0 8px', fontSize: '1.25rem' }}>
                    {isBM ? 'Reset Password' : 'Reset Password'}
                  </h3>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
                    {resetEmail && `Email: ${resetEmail}`}<br />
                    {isBM ? 'Masukkan kata laluan baru anda.' : 'Enter your new password.'}
                  </p>
                </div>

                <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>{isBM ? 'New Password' : 'New Password'}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 50px 14px 16px',
                          backgroundColor: colors.bgInput,
                          border: `2px solid ${colors.border}`,
                          borderRadius: '12px',
                          color: colors.textPrimary,
                          fontSize: '1rem',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                        placeholder={isBM ? "Kata laluan baru" : "New password"}
                        data-testid="reset-new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: colors.textMuted,
                          cursor: 'pointer',
                          padding: '4px',
                        }}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '6px' }}>
                      {isBM ? 'Min 8 aksara, 1 uppercase letter, 1 lowercase letter, 1 number, 1 symbol' : 'Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 symbol'}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>{isBM ? 'Confirm Password' : 'Confirm Password'}</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        backgroundColor: colors.bgInput,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        color: colors.textPrimary,
                        fontSize: '1rem',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      placeholder={isBM ? "Sahkan kata laluan" : "Confirm password"}
                      data-testid="reset-confirm-password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.accentGradient,
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '1rem',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(255, 64, 129, 0.4)',
                    }}
                  >
                    {loading ? (isBM ? 'Mengemaskini...' : 'Updating...') : (isBM ? 'Reset Password' : 'Reset Password')}
                  </button>
                </form>
              </div>
            )}

            {/* Form - Only show when not in forgot/reset mode */}
            {!showForgotPassword && !showResetPassword && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {(!isLogin || inviteData) && (
                <div>
                  <label style={{
                    display: 'block',
                    color: colors.textSecondary,
                    fontSize: '0.875rem',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}>{isBM ? 'Nama' : 'Name'}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder={isBM ? "Nama penuh anda" : "Your full name"}
                    required={!isLogin || !!inviteData}
                    data-testid="register-name"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              )}

              <div>
                <label style={{
                  display: 'block',
                  color: colors.textSecondary,
                  fontSize: '0.875rem',
                  marginBottom: '8px',
                  fontWeight: '500',
                }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    backgroundColor: colors.bgInput,
                    border: `2px solid ${colors.border}`,
                    borderRadius: '12px',
                    color: colors.textPrimary,
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  placeholder="email@example.com"
                  required
                  data-testid="auth-email"
                  onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  color: colors.textSecondary,
                  fontSize: '0.875rem',
                  marginBottom: '8px',
                  fontWeight: '500',
                }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder={isLogin && !inviteData ? (isBM ? "Enter password" : "Enter password") : (isBM ? "Min. 8 characters" : "Min. 8 characters")}
                    required
                    data-testid="auth-password"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {showPassword ? <EyeOff style={{ width: '20px', height: '20px' }} /> : <Eye style={{ width: '20px', height: '20px' }} />}
                  </button>
                </div>
                
                {/* Password Strength Indicator - Only show on register */}
                {(!isLogin || inviteData) && (
                  <div style={{ marginTop: '10px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: '600' }}>
                      📋 {isBM ? 'Keperluan Password:' : 'Password Requirements:'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {[
                        { check: formData.password.length >= 8, label: isBM ? 'Minimum 8 aksara' : 'Minimum 8 characters' },
                        { check: /[A-Z]/.test(formData.password), label: isBM ? '1 huruf BESAR (A-Z)' : '1 uppercase (A-Z)' },
                        { check: /[a-z]/.test(formData.password), label: isBM ? '1 lowercase letter (a-z)' : '1 lowercase (a-z)' },
                        { check: /[0-9]/.test(formData.password), label: isBM ? '1 number (0-9)' : '1 number (0-9)' },
                        { check: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password), label: isBM ? '1 symbol (!@#$%^&*)' : '1 symbol (!@#$%^&*)' }
                      ].map((item, i) => (
                        <div key={i} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          fontSize: '12px',
                          color: item.check ? '#4ADE80' : (formData.password ? '#FF6B6B' : '#666')
                        }}>
                          <span style={{ width: '16px', textAlign: 'center' }}>
                            {item.check ? '✓' : (formData.password ? '✗' : '○')}
                          </span>
                          <span>{item.label}</span>
                        </div>
                      ))}
                    </div>
                    {formData.password && (
                      <div style={{ 
                        marginTop: '10px', 
                        paddingTop: '10px', 
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {(() => {
                          const checks = [
                            formData.password.length >= 8,
                            /[A-Z]/.test(formData.password),
                            /[a-z]/.test(formData.password),
                            /[0-9]/.test(formData.password),
                            /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
                          ];
                          const passed = checks.filter(Boolean).length;
                          if (passed === 5) return <span style={{ color: '#4ADE80' }}>✅ {isBM ? 'Password memenuhi semua syarat!' : 'Password meets all requirements!'}</span>;
                          return <span style={{ color: '#FF6B6B' }}>⚠️ {isBM ? `Sila lengkapkan ${5 - passed} syarat lagi` : `Please complete ${5 - passed} more requirements`}</span>;
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {(!isLogin || inviteData) && (
                <div>
                  <label style={{
                    display: 'block',
                    color: colors.textSecondary,
                    fontSize: '0.875rem',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}>{isBM ? 'No. Telefon (Opsional)' : 'Phone Number (Optional)'}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder="+60123456789"
                    data-testid="register-phone"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: colors.accentGradient,
                  color: '#FFFFFF',
                  fontWeight: '600',
                  fontSize: '1rem',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  marginTop: '8px',
                  boxShadow: '0 4px 15px rgba(255, 64, 129, 0.4)',
                }}
              >
                {loading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                ) : inviteData ? (
                  <>
                    <Gift style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Register with Invite' : 'Register with Invite'}
                  </>
                ) : isLogin ? (
                  <>
                    <LogIn style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Login' : 'Login'}
                  </>
                ) : (
                  <>
                    <UserPlus style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Daftar Sekarang' : 'Register Now'}
                  </>
                )}
              </button>

              {/* Forgot Password Link - Only show on login */}
              {isLogin && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setError(''); setSuccess(''); }}
                    data-testid="forgot-password-link"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    {isBM ? 'Forgot password?' : 'Forgot password?'}
                  </button>
                </div>
              )}
            </form>
            )}

            {/* Bottom Text - Hide when invite is active or in forgot/reset mode */}
            {!inviteData && !showForgotPassword && !showResetPassword && (
              <p style={{
                textAlign: 'center',
                color: colors.textMuted,
                fontSize: '0.875rem',
                marginTop: '24px',
              }}>
                {isLogin ? (
                  <>
                    Don't have an account?{' '}
                    <button
                      onClick={() => setIsLogin(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        padding: 0,
                      }}
                    >
                      Register now
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      onClick={() => setIsLogin(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        padding: 0,
                      }}
                    >
                      Login
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Features */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          marginTop: '32px',
          flexWrap: 'wrap',
        }}>
          {features.map((feature, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: colors.textMuted,
              fontSize: '0.8rem',
            }}>
              <feature.icon style={{ width: '16px', height: '16px', color: colors.accent }} />
              <span>{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Powered by */}
        <p data-testid="powered-by" style={{ textAlign: 'center', marginTop: '24px', fontSize: '12px', color: '#94a3b8', letterSpacing: '1px' }}>
          Powered by <span style={{ color: '#FF4081', fontWeight: '700' }}>Underads</span>
        </p>
      </div>

      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input::placeholder {
          color: #666666;
        }
      `}</style>
    </div>
  );
};

export default AuthPage;
